import type React from 'react';
import { useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormField,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Select,
  TextButton,
} from '@zillow/constellation-10';

import { formatDate } from '@common/paymentsUtils/dateUtils';
import ConfirmStopPayments from '@rental-manager/pages/Dashboard/EditPaymentLengthModal/modules/ConfirmStopPayments';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import userPersonas from '@common/constants/userPersonas';
import { isStartDateTooSoon, makeDueDates, STORE_DATE_FORMAT } from '@common/paymentsUtils/selectOptionsBuilder';
import type { EndDatesInterface, StartDatesInterface } from '@common/types';
import { TERM_LENGTH_END_DATE_OPTIONS_COUNT } from '@rental-manager/constants/PaymentConstants';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const START_DATE_HELP_TEXT =
  'Enter your tenant’s monthly rent payment date. This may be different from the payment date for move-in costs, such as a security deposit.';
const START_DATE_TOO_SOON_TEXT =
  'Your renter may not have time to set up a payment before this start date. Their first payment may take a few additional days to reach you.';

interface Props {
  showOneTimePayment?: boolean;
  today?: Date | string;
  firstDueDate?: Date | string;
  numberOfPayments?: number;
  monthToMonth?: boolean;
  paymentDayOfTheMonth: number;
  editDayOfTheMonth?: any;
  monthlyRent?: string;
  startDateFromLease?: string;
  endDateFromLease?: string;
  editMode?: boolean;
  onSubmit?: (requestPayload: PaymentSettings) => any;
  onClose?: () => void;
  onCancel?: () => void;
  onBack?: () => void;
  requestStopPayments?: (rentTermsKey: string) => Promise<any>;
  rentTermsKey?: string;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}

interface PaymentSettings {
  monthToMonth: boolean;
  firstDueDate: Date | string;
  monthlyRent: string;
  paymentDayOfTheMonth: number;
  editDayOfTheMonth?: number | null;
  numberOfPayments?: number;
  paymentEndDate?: string;
}

// FIXME: This is a dupe of EditPaymentLengthModal and NewTenantTermLength components
const TermLengthPage: React.FC<Props> = ({
  currentProperty,
  editDayOfTheMonth,
  editMode = false,
  endDateFromLease = '',
  firstDueDate = new Date(),
  monthlyRent = '',
  monthToMonth: initialMonthToMonth = false,
  numberOfPayments = 12,
  onBack = () => {},
  onCancel = () => {},
  onClose = () => {},
  onSubmit = () => {},
  paymentDayOfTheMonth,
  rentTermsKey = '',
  requestStopPayments = () => Promise.resolve(),
  showOneTimePayment = false,
  startDateFromLease = '',
  today = new Date(),
  user,
}) => {
  const effectivePaymentDayOfTheMonth = editMode && editDayOfTheMonth ? editDayOfTheMonth : paymentDayOfTheMonth;

  const startDates = useMemo(
    () => makeDueDates(effectivePaymentDayOfTheMonth, today, TERM_LENGTH_END_DATE_OPTIONS_COUNT),
    [effectivePaymentDayOfTheMonth, today],
  );

  const initialSelectedStartDate = useMemo(() => {
    return (
      startDates.find((startDate) => startDate.date === startDateFromLease) ||
      startDates.find((startDate) => startDate.date === formatDate(firstDueDate, STORE_DATE_FORMAT)) ||
      startDates[0]
    );
  }, [startDates, startDateFromLease, firstDueDate]);

  const initialSelectedEndDate = useMemo(() => {
    return (
      initialSelectedStartDate.endDates.find((endDate) => endDate.date === endDateFromLease) ||
      initialSelectedStartDate.endDates.find((endDate) => endDate.payments === numberOfPayments) ||
      initialSelectedStartDate.endDates[0]
    );
  }, [initialSelectedStartDate, endDateFromLease, numberOfPayments]);

  const [selectedStartDate, setSelectedStartDate] = useState<StartDatesInterface>(initialSelectedStartDate);
  const [selectedEndDate, setSelectedEndDate] = useState<EndDatesInterface>(initialSelectedEndDate);
  const [monthToMonth, setMonthToMonth] = useState(initialMonthToMonth);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmStopPayments, setShowConfirmStopPayments] = useState(false);

  const handleSubmit = async (): Promise<any> => {
    return Promise.resolve()
      .then(() => setIsLoading(true))
      .then(() => {
        const paymentSettings: PaymentSettings = {
          firstDueDate: selectedStartDate.date,
          monthToMonth,
          paymentDayOfTheMonth: effectivePaymentDayOfTheMonth,
          monthlyRent,
        };

        // dispatch the new paymentDayOfTheMonth now.
        if (editMode && editDayOfTheMonth) {
          paymentSettings.editDayOfTheMonth = null;
          paymentSettings.paymentDayOfTheMonth = editDayOfTheMonth;
        }

        if (!monthToMonth) {
          paymentSettings.numberOfPayments = selectedEndDate.payments;
          paymentSettings.paymentEndDate = selectedEndDate.date;
        }

        return onSubmit(paymentSettings);
      })
      .then((response = {}) => {
        if (response.error) {
          setIsLoading(false);
        } else {
          IreneOnboardingCSEvents.emitContinueFromTermLengthPageEvent({ currentProperty, user });
        }
      });
  };

  const handleConfirmStopPayments = (): Promise<any> =>
    Promise.resolve()
      .then(() => setIsLoading(true))
      .then(() => requestStopPayments(rentTermsKey))
      .then(({ err } = {}) => {
        if (err) {
          setIsLoading(false);
        }
      });

  const handleClose = (): void => {
    IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'term_length',
    });
    onClose();
  };

  const handleGoToPreviousPage = (): void => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'term_length',
    });
    onBack();
  };

  const handleChangeStartDate = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const selectedStartDateNew = startDates[e.currentTarget.selectedIndex];
    const selectedEndDateNew =
      selectedStartDateNew.endDates.find((endDate) => endDate.payments === selectedEndDate.payments) ||
      selectedStartDateNew.endDates[0];

    setSelectedStartDate(selectedStartDateNew);
    setSelectedEndDate(selectedEndDateNew);
  };

  const handleChangeEndDate = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const selectedEndDateNew = selectedStartDate.endDates[e.currentTarget.selectedIndex];
    setSelectedEndDate(selectedEndDateNew);
  };

  const handleToggleMonthToMonth = (): void => {
    setMonthToMonth(!monthToMonth);
  };

  const { wasAdjusted, date: startDate } = selectedStartDate;
  const startDateLabel = paymentDayOfTheMonth === 1 ? 'First full rent payment' : 'First full rent payment starts';
  const startDateTooSoon = isStartDateTooSoon(startDate, new Date());

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      aside={<FaqComponent faqs={FaqConstants.ONBOARDING_V2.TERM_LENGTH_PAGE} />}
      onRequestClose={handleClose}
      header="Payment setup"
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          {showConfirmStopPayments ? (
            <ConfirmStopPayments
              onConfirm={handleConfirmStopPayments}
              onBack={() => setShowConfirmStopPayments(false)}
            />
          ) : (
            <>
              <Heading level={3} textStyle="body-lg-bold">
                How long will you be collecting rent?
              </Heading>
              <FormField
                css={{ maxWidth: '36rem' }}
                required
                label={<Label>{startDateLabel}</Label>}
                control={
                  <Select value={selectedStartDate.date} onChange={handleChangeStartDate}>
                    {startDates.map(({ date, display }) => (
                      <option key={date} value={date}>
                        {display}
                      </option>
                    ))}
                  </Select>
                }
                description={<FormHelp>{START_DATE_HELP_TEXT}</FormHelp>}
              />
              {startDateTooSoon && <Alert tone="warning">{START_DATE_TOO_SOON_TEXT}</Alert>}
              {wasAdjusted && (
                <Alert tone="info">
                  This month has fewer than {paymentDayOfTheMonth} days, so we selected the last day of the month by
                  default
                </Alert>
              )}
              <Box css={{ display: 'grid', gridTemplateRows: 'auto', gap: 'tight' }}>
                <FormField
                  css={{ maxWidth: '36rem' }}
                  disabled={monthToMonth}
                  required
                  label={<Label>Payment period ends</Label>}
                  control={
                    <Select value={selectedEndDate.payments} onChange={handleChangeEndDate}>
                      {selectedStartDate.endDates.map(({ date, display, payments }) => (
                        <option key={date} value={payments}>
                          {display}
                        </option>
                      ))}
                    </Select>
                  }
                />
                <LabeledControl
                  control={<Checkbox checked={monthToMonth} onChange={handleToggleMonthToMonth} />}
                  label={<Label>No end date or month-to-month</Label>}
                />
              </Box>
            </>
          )}
        </Box>
      }
      backAction={{ onClick: handleGoToPreviousPage }}
      footerAriaLabel="Term length actions"
      footerEnd={
        <>
          {editMode && <TextButton onClick={onCancel}>Cancel</TextButton>}
          {showOneTimePayment && (
            <Button emphasis="outlined" onClick={() => setShowConfirmStopPayments(true)}>
              Stop payments
            </Button>
          )}
          <DebouncedButton onClick={handleSubmit} loading={isLoading}>
            {editMode ? 'Update' : 'Continue'}
          </DebouncedButton>
        </>
      }
    />
  );
};

export default TermLengthPage;
